import { Fragment, useEffect, useState } from "react";
import api, { errMsg } from "../api";
import { useApp } from "../context/AppContext";
import { INVOICE_STATUS, MODULES, ORDER_TYPE, ORIGINES, clientName, fmtCHF, fmtDate, orderSummary } from "../utils";
import { toast } from "sonner";
import { CalendarRange, Eye, BadgeCheck, FileDown, ListChecks, Trash2, CalendarClock, FileSpreadsheet } from "lucide-react";
import ClientCombobox from "../components/ClientCombobox";

export default function Invoices() {
  const { user } = useApp();
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [monthlyOpen, setMonthlyOpen] = useState(false);
  const [monthForm, setMonthForm] = useState({ month: new Date().toISOString().slice(0, 7), client_id: "" });
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkOrders, setBulkOrders] = useState([]);
  const [bulkChecked, setBulkChecked] = useState(new Set());
  const [bulkForm, setBulkForm] = useState({ client_id: "", issue_date: "" });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [detail, setDetail] = useState(null);
  const [payTarget, setPayTarget] = useState(null);
  const [payMethod, setPayMethod] = useState("virement");
  const [statusFilter, setStatusFilter] = useState("");
  const [monthFilter, setMonthFilter] = useState("");
  const [clientFilter, setClientFilter] = useState("");
  const [origineFilter, setOrigineFilter] = useState("");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [sentFilter, setSentFilter] = useState(false);
  const [sentTarget, setSentTarget] = useState(null);
  const [sentForm, setSentForm] = useState({ date: "" });
  const [issueTarget, setIssueTarget] = useState(null);
  const [issueForm, setIssueForm] = useState({ date: "" });
  const [unexportedOnly, setUnexportedOnly] = useState(false);
  const [winbizOpen, setWinbizOpen] = useState(false);
  const [winbizForm, setWinbizForm] = useState({ from: "", to: "", onlyNew: true });

  const load = () => {
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (monthFilter) params.month = monthFilter;
    if (clientFilter) params.client_id = clientFilter;
    if (origineFilter) params.origine = origineFilter;
    if (overdueOnly) params.overdue = true;
    if (sentFilter) params.sent = "false";
    api.get("/invoices", { params }).then((r) => setInvoices(r.data));
  };
  useEffect(() => { load(); }, [statusFilter, monthFilter, clientFilter, origineFilter, overdueOnly, sentFilter]);
  useEffect(() => { api.get("/clients").then((r) => setClients(r.data)); }, []);

  const generateMonthly = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/invoices/generate-monthly", { month: monthForm.month, client_id: monthForm.client_id || null });
      if (r.data.count === 0) toast.info("Aucun bon réalisé non facturé pour ce mois");
      else toast.success(`${r.data.count} facture(s) créée(s) : ${r.data.created.map((c) => c.number).join(", ")}`);
      setMonthlyOpen(false);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const openBulk = () => {
    setBulkForm({ client_id: "", issue_date: new Date().toISOString().slice(0, 10) });
    setBulkOrders([]);
    setBulkChecked(new Set());
    setBulkOpen(true);
  };

  const loadBulkOrders = async (clientId) => {
    setBulkForm((p) => ({ ...p, client_id: clientId }));
    setBulkOrders([]);
    setBulkChecked(new Set());
    if (!clientId) return;
    try {
      const r = await api.get("/orders", { params: { client_id: clientId, status: "realise" } });
      const open = r.data.filter((o) => !o.invoice_id).sort((a, b) => (a.scheduled_date || "").localeCompare(b.scheduled_date || ""));
      setBulkOrders(open);
      setBulkChecked(new Set(open.map((o) => o.id)));
    } catch (err) { toast.error(errMsg(err)); }
  };

  const toggleBulk = (id) => setBulkChecked((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const bulkTotal = bulkOrders.filter((o) => bulkChecked.has(o.id)).reduce((s, o) => s + (o.amounts?.total_ht || 0), 0);
  const bulkFuture = !!bulkForm.issue_date && bulkForm.issue_date > new Date().toISOString().slice(0, 10);
  const shown = unexportedOnly ? invoices.filter((i) => !i.winbiz_number) : invoices;

  const submitWinbiz = async (e) => {
    e.preventDefault();
    try {
      const r = await api.get("/invoices/export-winbiz", {
        params: { date_from: winbizForm.from, date_to: winbizForm.to, only_not_exported: winbizForm.onlyNew },
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "bizexdoc.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("bizexdoc.csv téléchargé");
      setWinbizOpen(false);
      load();
    } catch (err) {
      let msg = "Erreur inattendue";
      try {
        if (err.response?.data instanceof Blob) msg = JSON.parse(await err.response.data.text())?.detail || msg;
        else msg = errMsg(err);
      } catch { /* noop */ }
      toast.error(msg);
    }
  };

  const submitBulk = async (e) => {
    e.preventDefault();
    if (bulkFuture) { toast.error("La date de facture ne peut pas être dans le futur"); return; }
    try {
      const r = await api.post("/invoices/generate-client", {
        client_id: bulkForm.client_id,
        order_ids: bulkOrders.filter((o) => bulkChecked.has(o.id)).map((o) => o.id),
        issue_date: bulkForm.issue_date,
      });
      toast.success(`Facture ${r.data.number} créée (${r.data.lines.length} bon(s))`);
      setBulkOpen(false);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/invoices/${deleteTarget.id}`);
      toast.success(`Facture ${deleteTarget.number} supprimée — les bons liés sont redevenus facturables`);
      if (detail?.id === deleteTarget.id) setDetail(null);
      setDeleteTarget(null);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const openSentModal = (inv) => {
    setSentForm({ date: inv.sent_date || new Date().toISOString().slice(0, 10) });
    setSentTarget(inv);
  };

  const sentFuture = !!sentForm.date && sentForm.date > new Date().toISOString().slice(0, 10);
  const sentBeforeIssue = !!sentTarget && !!sentForm.date && sentForm.date < (sentTarget.issue_date || "").slice(0, 10);

  const submitSent = async (e) => {
    e.preventDefault();
    if (sentFuture) { toast.error("La date d'envoi ne peut pas être dans le futur"); return; }
    if (sentBeforeIssue) { toast.error("La date d'envoi ne peut pas être antérieure à la date de facture"); return; }
    try {
      const r = await api.put(`/invoices/${sentTarget.id}/sent`, { sent_date: sentForm.date });
      toast.success(`Facture ${sentTarget.number} marquée comme envoyée`);
      if (detail?.id === sentTarget.id) setDetail({ ...detail, sent_date: r.data.sent_date });
      setSentTarget(null);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const removeSent = async (inv) => {
    try {
      await api.put(`/invoices/${inv.id}/sent`, { sent_date: null });
      toast.success(`Marquage d'envoi retiré pour ${inv.number}`);
      if (detail?.id === inv.id) { const d = { ...detail }; delete d.sent_date; setDetail(d); }
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const openDetail = async (inv) => {
    const r = await api.get(`/invoices/${inv.id}`);
    const rates = (r.data.lines || []).map((l) => l.discount_percent || 0);
    const nz = rates.filter((x) => x > 0);
    r.data._uniformRate = nz.length > 0 && nz.length === rates.length && rates.every((x) => x === nz[0]) ? nz[0] : null;
    setDetail(r.data);
  };

  const openIssueModal = (inv) => {
    setIssueForm({ date: (inv.issue_date || "").slice(0, 10) });
    setIssueTarget(inv);
  };

  const issueFuture = !!issueForm.date && issueForm.date > new Date().toISOString().slice(0, 10);
  const issueAfterSent = !!issueTarget?.sent_date && !!issueForm.date && issueForm.date > issueTarget.sent_date;

  const submitIssueDate = async (e) => {
    e.preventDefault();
    if (issueFuture) { toast.error("La date de facture ne peut pas être dans le futur"); return; }
    if (issueAfterSent) { toast.error("La date d'envoi ne peut pas précéder la date de facture"); return; }
    try {
      await api.put(`/invoices/${issueTarget.id}/issue-date`, { issue_date: issueForm.date });
      toast.success(`Date de la facture ${issueTarget.number} modifiée`);
      if (detail?.id === issueTarget.id) setDetail({ ...detail, issue_date: issueForm.date + (detail.issue_date || "").slice(10) });
      setIssueTarget(null);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const markPaid = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/invoices/${payTarget.id}/pay`, { payment_method: payMethod });
      toast.success(`Facture ${payTarget.number} marquée payée`);
      setPayTarget(null);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const openPdf = async (inv) => {
    try {
      const r = await api.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${inv.number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success(`PDF ${inv.number} téléchargé`);
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5" data-testid="invoices-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-xl font-semibold text-slate-900">Factures</h1>
        <div className="flex items-center gap-2">
          <button data-testid="generate-client-invoice-button" onClick={openBulk}
            className="flex items-center gap-2 bg-white border border-slate-300 text-slate-700 rounded-lg px-4 py-2 text-sm font-semibold hover:bg-slate-100 transition-colors">
            <ListChecks size={16} /> Facturer les bons ouverts
          </button>
          <button data-testid="generate-monthly-invoices-button" onClick={() => setMonthlyOpen(true)}
            className="flex items-center gap-2 bg-slate-900 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-slate-800 transition-colors">
            <CalendarRange size={16} /> Facturation mensuelle
          </button>
          {user?.role !== "employe" && (
            <button data-testid="export-winbiz-button"
              onClick={() => { const t = new Date().toISOString().slice(0, 10); setWinbizForm({ from: `${t.slice(0, 8)}01`, to: t, onlyNew: true }); setWinbizOpen(true); }}
              className="flex items-center gap-2 bg-white border border-slate-300 text-slate-700 rounded-lg px-4 py-2 text-sm font-semibold hover:bg-slate-100 transition-colors">
              <FileSpreadsheet size={16} /> Export Winbiz
            </button>
          )}
        </div>
      </div>

      <div className="flex gap-2 flex-wrap" data-testid="invoice-status-filters">
        {[["", "Toutes"], ...Object.entries(INVOICE_STATUS).map(([k, v]) => [k, v.label])].map(([k, label]) => (
          <button key={k} data-testid={`invoice-filter-${k || "all"}`} onClick={() => setStatusFilter(k)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${statusFilter === k ? "bg-slate-900 text-white border-transparent" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-100"}`}>
            {label}
          </button>
        ))}
        <button data-testid="invoice-filter-unsent" onClick={() => setSentFilter((v) => !v)}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${sentFilter ? "bg-amber-600 text-white border-transparent" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-100"}`}>
          Non envoyées
        </button>
        <button data-testid="invoice-filter-unexported" onClick={() => setUnexportedOnly((v) => !v)}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${unexportedOnly ? "bg-amber-600 text-white border-transparent" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-100"}`}>
          Non exportées (Winbiz)
        </button>
      </div>

      <div className="flex gap-4 flex-wrap items-end bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-testid="invoice-advanced-filters">
        <div>
          <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Mois</label>
          <input data-testid="invoice-filter-month" type="month" value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)}
            className="mt-1 block rounded-lg border border-slate-300 px-3 py-1.5 text-sm bg-white" />
        </div>
        <div>
          <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Client</label>
          <ClientCombobox testid="invoice-filter-client" clients={clients} value={clientFilter} onChange={setClientFilter} />
        </div>
        <div>
          <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Origine</label>
          <select data-testid="invoice-filter-origine" value={origineFilter} onChange={(e) => setOrigineFilter(e.target.value)}
            className="mt-1 block rounded-lg border border-slate-300 px-3 py-1.5 text-sm bg-white">
            <option value="">Toutes</option>
            {Object.entries(ORIGINES).map(([k, o]) => <option key={k} value={k}>{o.label}</option>)}
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700 pb-2">
          <input data-testid="invoice-filter-overdue" type="checkbox" checked={overdueOnly} onChange={(e) => setOverdueOnly(e.target.checked)} />
          En retard (+30 j, impayées)
        </label>
        {(monthFilter || clientFilter || origineFilter || overdueOnly || sentFilter) && (
          <button data-testid="invoice-filter-reset" onClick={() => { setMonthFilter(""); setClientFilter(""); setOrigineFilter(""); setOverdueOnly(false); setSentFilter(false); }}
            className="text-xs text-slate-500 hover:text-slate-900 underline pb-2">Réinitialiser</button>
        )}
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-100">
              <th className="px-5 py-3">N°</th><th className="px-5 py-3">Client</th><th className="px-5 py-3">Date</th>
              <th className="px-5 py-3">Total HT</th><th className="px-5 py-3">TVA</th><th className="px-5 py-3">Total TTC</th>
              <th className="px-5 py-3">Statut</th><th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-testid="invoices-table">
            {shown.map((i) => {
              const st = INVOICE_STATUS[i.status];
              const isOverdue = i.status === "emise" && (Date.now() - new Date(i.issue_date).getTime()) > 30 * 864e5;
              return (
                <tr key={i.id} data-testid={`invoice-row-${i.id}`} className="hover:bg-slate-50">
                  <td className="px-5 py-3 font-mono2 text-xs font-semibold">{i.number}</td>
                  <td className="px-5 py-3 font-medium text-slate-900">{i.client_name}
                    <div><span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${ORIGINES[i.origine || "saphir_immo"].cls}`}>{ORIGINES[i.origine || "saphir_immo"].label}</span></div>
                  </td>
                  <td className="px-5 py-3 text-slate-600">{fmtDate(i.issue_date)}
                    {i.sent_date
                      ? <div className="text-[11px] font-medium text-emerald-700" data-testid={`invoice-sent-${i.id}`}>Envoyée au client le {fmtDate(i.sent_date)}</div>
                      : <div className="text-[11px] text-slate-400" data-testid={`invoice-sent-${i.id}`}>Non envoyée</div>}
                    {i.winbiz_number != null && (
                      <div className="text-[11px] font-medium text-sky-700" data-testid={`invoice-winbiz-${i.id}`}>Exportée vers Winbiz le {fmtDate(i.winbiz_exported_at)}</div>
                    )}
                  </td>
                  <td className="px-5 py-3 text-slate-700">{fmtCHF(i.total_ht)}</td>
                  <td className="px-5 py-3 text-slate-500">{fmtCHF(i.tva_amount)}</td>
                  <td className="px-5 py-3 font-semibold text-slate-900">{fmtCHF(i.total_payable ?? i.total_ttc)}</td>
                  <td className="px-5 py-3">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${st.cls}`} data-testid={`invoice-status-${i.id}`}>{st.label}{i.status === "payee" && i.payment_method ? ` · ${i.payment_method}` : ""}</span>
                    {isOverdue && <span className="ml-1 text-xs font-medium px-2 py-0.5 rounded-md bg-red-50 text-red-700" data-testid={`invoice-overdue-${i.id}`}>En retard</span>}
                  </td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    <button data-testid={`view-invoice-${i.id}`} title="Détail" onClick={() => openDetail(i)} className="text-slate-400 hover:text-slate-900 p-1"><Eye size={16} /></button>
                    <button data-testid={`pdf-invoice-${i.id}`} title="PDF" onClick={() => openPdf(i)} className="text-slate-400 hover:text-slate-900 p-1"><FileDown size={16} /></button>
                    {i.status === "emise" && (
                      <button data-testid={`pay-invoice-${i.id}`} title="Marquer payée" onClick={() => { setPayMethod("virement"); setPayTarget(i); }} className="text-emerald-600 hover:text-emerald-800 p-1"><BadgeCheck size={16} /></button>
                    )}
                    {user?.role === "admin" && (
                      <button data-testid={`issue-date-invoice-${i.id}`} title="Modifier la date" onClick={() => openIssueModal(i)} className="text-slate-400 hover:text-slate-900 p-1"><CalendarClock size={16} /></button>
                    )}
                    {user?.role === "admin" && (
                      <button data-testid={`delete-invoice-${i.id}`} title="Supprimer" onClick={() => setDeleteTarget(i)} className="text-slate-400 hover:text-red-600 p-1"><Trash2 size={16} /></button>
                    )}
                  </td>
                </tr>
              );
            })}
            {shown.length === 0 && <tr><td colSpan={8} className="px-5 py-8 text-center text-slate-400">Aucune facture.</td></tr>}
          </tbody>
        </table>
      </div>

      {monthlyOpen && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50" data-testid="monthly-invoice-modal">
          <form onSubmit={generateMonthly} className="bg-white rounded-lg border border-slate-200 shadow-lg w-full max-w-md p-6 space-y-4">
            <h2 className="text-base font-semibold text-slate-900">Facturation mensuelle groupée</h2>
            <p className="text-xs text-slate-500">Regroupe tous les bons réalisés non facturés du mois en une facture par client.</p>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Mois</label>
              <input data-testid="monthly-invoice-month" type="month" required value={monthForm.month} onChange={(e) => setMonthForm({ ...monthForm, month: e.target.value })}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Client (optionnel)</label>
              <select data-testid="monthly-invoice-client" value={monthForm.client_id} onChange={(e) => setMonthForm({ ...monthForm, client_id: e.target.value })}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white">
                <option value="">— Tous les clients —</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" data-testid="monthly-invoice-cancel" onClick={() => setMonthlyOpen(false)} className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100">Annuler</button>
              <button type="submit" data-testid="monthly-invoice-submit" className="px-4 py-2 rounded-lg text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800">Générer les factures</button>
            </div>
          </form>
        </div>
      )}

      {detail && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50" data-testid="invoice-pdf-modal">
          <div className="bg-white rounded-lg border border-slate-200 shadow-lg w-full max-w-2xl p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-slate-900">Facture {detail.number}</h2>
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${INVOICE_STATUS[detail.status].cls}`}>{INVOICE_STATUS[detail.status].label}</span>
            </div>
            <div className="text-sm text-slate-600">{detail.client_name} · {fmtDate(detail.issue_date)} · <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${ORIGINES[detail.origine || "saphir_immo"].cls}`}>{ORIGINES[detail.origine || "saphir_immo"].label}</span> · Compte tiers <span className="font-mono2">{detail.compte_tiers}</span></div>
            {user?.role !== "employe" && detail.winbiz_number != null && (
              <div className="text-xs text-slate-500" data-testid="invoice-detail-winbiz">
                N° Winbiz : <span className="font-mono2 font-semibold">{detail.winbiz_number}</span>
                {detail.winbiz_exported_at ? ` — exportée le ${fmtDate(detail.winbiz_exported_at)}` : ""}
                {" "}(usage comptable interne — jamais imprimé sur le document client)
              </div>
            )}
            <div className="flex items-center gap-3 text-sm" data-testid="invoice-detail-sent">
              {detail.sent_date
                ? <span className="font-medium text-emerald-700">Envoyée au client le {fmtDate(detail.sent_date)}</span>
                : <span className="text-slate-400">Non envoyée</span>}
              <button data-testid="invoice-sent-mark" onClick={() => openSentModal(detail)}
                className="text-xs font-semibold text-sky-700 hover:text-sky-900 underline">
                {detail.sent_date ? "Modifier la date d'envoi" : "Marquer comme envoyée"}
              </button>
              {detail.sent_date && (
                <button data-testid="invoice-sent-remove" onClick={() => removeSent(detail)}
                  className="text-xs font-semibold text-red-600 hover:text-red-800 underline">Retirer le marquage</button>
              )}
            </div>
            <table className="w-full text-sm" data-testid="invoice-detail-lines">
              <thead><tr className="text-left text-xs font-semibold text-slate-500 uppercase border-b border-slate-100">
                <th className="py-2">Description</th><th className="py-2">Compte</th><th className="py-2 text-right">HT</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {detail.lines.map((l, i) => {
                  const ref = (detail.order_refs || {})[l.order_id] || {};
                  return (
                    <Fragment key={i}>
                      <tr data-testid={`invoice-order-ref-${i}`}>
                        <td colSpan={3} className="pt-3 pb-1 text-xs font-semibold text-slate-500">
                          Bon de commande {ref.order_number || l.order_number} du {fmtDate(ref.scheduled_date || l.date)}
                        </td>
                      </tr>
                      <tr>
                        <td className="py-2 pr-2">{l.description.split(" — bon ")[0]}
                          {!detail._uniformRate && (l.discount_percent || 0) > 0 && (
                            <span className="ml-2 text-[11px] font-semibold text-emerald-700" data-testid={`invoice-line-rabais-${i}`}>rabais {l.discount_percent} %</span>
                          )}
                          {l.materials?.map((m, j) => <div key={j} className="text-xs text-slate-400 italic">+ Matériel : {m.name} ({fmtCHF(m.price)})</div>)}
                        </td>
                        <td className="py-2 font-mono2 text-xs">{l.compte_comptable}</td>
                        <td className="py-2 text-right font-medium">{fmtCHF(l.total_ht)}</td>
                      </tr>
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
            <div className="border-t border-slate-200 pt-3 space-y-1 text-sm">
              <Row label="Sous-total HT" value={detail.subtotal_ht} />
              {detail.discount_amount > 0 && <Row label={detail._uniformRate ? `Rabais ${detail._uniformRate} %` : "Rabais"} value={-detail.discount_amount} />}
              <Row label="Total HT" value={detail.total_ht} />
              <Row label={`TVA (${detail.tva_rate} %)`} value={detail.tva_amount} />
              <Row label="Total TTC" value={detail.total_ttc} />
              {(detail.rounding_adjustment ?? 0) !== 0 && <Row label="Arrondi" value={detail.rounding_adjustment} />}
              <div className="flex justify-between text-base font-semibold text-slate-900 pt-1" data-testid="invoice-detail-total">
                <span>TOTAL À PAYER</span><span>{fmtCHF(detail.total_payable ?? detail.total_ttc)}</span>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              {user?.role === "admin" && (
                <div className="mr-auto flex gap-2">
                  <button data-testid="invoice-detail-issue-date" onClick={() => openIssueModal(detail)}
                    className="px-4 py-2 rounded-lg text-sm font-semibold bg-slate-100 text-slate-800 hover:bg-slate-200">Modifier la date</button>
                  <button data-testid="invoice-detail-delete" onClick={() => setDeleteTarget(detail)}
                    className="px-4 py-2 rounded-lg text-sm font-semibold bg-red-50 text-red-700 hover:bg-red-100">Supprimer</button>
                </div>
              )}
              <button data-testid="invoice-detail-pdf" onClick={() => openPdf(detail)} className="px-4 py-2 rounded-lg text-sm font-semibold bg-slate-100 text-slate-800 hover:bg-slate-200">Ouvrir le PDF</button>
              <button data-testid="invoice-detail-close" onClick={() => setDetail(null)} className="px-4 py-2 rounded-lg text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800">Fermer</button>
            </div>
          </div>
        </div>
      )}

      {payTarget && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50" data-testid="pay-invoice-modal">
          <form onSubmit={markPaid} className="bg-white rounded-lg border border-slate-200 shadow-lg w-full max-w-sm p-6 space-y-4">
            <h2 className="text-base font-semibold text-slate-900">Encaisser {payTarget.number}</h2>
            <div className="text-sm text-slate-600">Montant : <b>{fmtCHF(payTarget.total_payable ?? payTarget.total_ttc)}</b></div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Mode de paiement</label>
              <select data-testid="pay-method-select" value={payMethod} onChange={(e) => setPayMethod(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white">
                <option value="cash">Cash</option><option value="virement">Virement</option><option value="carte">Carte</option>
              </select>
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" data-testid="pay-cancel" onClick={() => setPayTarget(null)} className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100">Annuler</button>
              <button type="submit" data-testid="pay-submit" className="px-4 py-2 rounded-lg text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700">Confirmer l'encaissement</button>
            </div>
          </form>
        </div>
      )}

      {bulkOpen && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50" data-testid="bulk-invoice-modal">
          <form onSubmit={submitBulk} className="bg-white rounded-lg border border-slate-200 shadow-lg w-full max-w-2xl p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-base font-semibold text-slate-900">Facturer les bons ouverts d'un client</h2>
            <p className="text-xs text-slate-500">Tous les bons « Réalisé » non facturés du client, toutes périodes et tous modules confondus (refacturations incluses), regroupés en une seule facture.</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Client *</label>
                <select data-testid="bulk-invoice-client" required value={bulkForm.client_id} onChange={(e) => loadBulkOrders(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white">
                  <option value="">— Choisir —</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{clientName(c)}</option>)}
                </select>
              </div>
              <DateField label="Date de facture *" testid="bulk-invoice-date" required value={bulkForm.issue_date} onChange={(v) => setBulkForm((p) => ({ ...p, issue_date: v }))} />
            </div>
            {bulkFuture && <p className="text-xs font-semibold text-red-600" data-testid="bulk-invoice-future-error">Une date future est refusée.</p>}
            {bulkForm.client_id && (
              <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="bulk-invoice-orders">
                {bulkOrders.length === 0 ? (
                  <p className="px-4 py-6 text-center text-sm text-slate-400">Aucun bon réalisé à facturer pour ce client.</p>
                ) : (
                  <table className="w-full text-sm">
                    <tbody className="divide-y divide-slate-100">
                      {bulkOrders.map((o) => (
                        <tr key={o.id} data-testid={`bulk-order-row-${o.id}`} className="hover:bg-slate-50">
                          <td className="px-3 py-2 w-8">
                            <input data-testid={`bulk-order-check-${o.id}`} type="checkbox" checked={bulkChecked.has(o.id)} onChange={() => toggleBulk(o.id)} />
                          </td>
                          <td className="px-2 py-2 whitespace-nowrap">
                            <span className="font-mono2 text-xs font-semibold text-slate-900">{o.order_number}</span>
                            {o.order_type === "refacturation"
                              ? <span className={`ml-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${ORDER_TYPE.refacturation.cls}`}>Refacturation</span>
                              : <span className={`ml-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${MODULES[o.module].badge}`}>{MODULES[o.module].label}</span>}
                          </td>
                          <td className="px-2 py-2 text-slate-600 whitespace-nowrap">{fmtDate(o.scheduled_date)}</td>
                          <td className="px-2 py-2 text-slate-600 truncate max-w-[220px]">{orderSummary(o)}</td>
                          <td className="px-3 py-2 text-right font-medium text-slate-900 whitespace-nowrap">{fmtCHF(o.amounts?.total_ht || 0)} HT</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
            {bulkOrders.length > 0 && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm flex justify-between" data-testid="bulk-invoice-total">
                <span className="text-slate-600">{bulkChecked.size} bon(s) sélectionné(s) — Total HT (avant rabais)</span>
                <span className="font-semibold text-slate-900">{fmtCHF(bulkTotal)}</span>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" data-testid="bulk-invoice-cancel" onClick={() => setBulkOpen(false)} className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100">Annuler</button>
              <button type="submit" data-testid="bulk-invoice-submit" disabled={!bulkForm.client_id || bulkChecked.size === 0 || bulkFuture}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-40">Créer la facture</button>
            </div>
          </form>
        </div>
      )}

      {winbizOpen && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50" data-testid="winbiz-export-modal">
          <form onSubmit={submitWinbiz} className="bg-white rounded-lg border border-slate-200 shadow-lg w-full max-w-sm p-6 space-y-4">
            <h2 className="text-base font-semibold text-slate-900">Export Winbiz (bizexdoc.csv)</h2>
            <p className="text-xs text-slate-500">Factures de vente par date de facture. Chaque facture reçoit un numéro Winbiz (7 chiffres), attribué une seule fois. Pour ré-exporter aussi les factures déjà exportées de la période (numéro Winbiz inchangé), décochez l'option ci-dessous.</p>
            <DateField label="Du (date de facture) *" testid="winbiz-from" required value={winbizForm.from} onChange={(v) => setWinbizForm((p) => ({ ...p, from: v }))} />
            <DateField label="Au (date de facture) *" testid="winbiz-to" required value={winbizForm.to} onChange={(v) => setWinbizForm((p) => ({ ...p, to: v }))} />
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input data-testid="winbiz-only-new" type="checkbox" checked={winbizForm.onlyNew} onChange={(e) => setWinbizForm((p) => ({ ...p, onlyNew: e.target.checked }))} />
              Uniquement les factures non encore exportées
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" data-testid="winbiz-cancel" onClick={() => setWinbizOpen(false)} className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100">Annuler</button>
              <button type="submit" data-testid="winbiz-submit" disabled={!winbizForm.from || !winbizForm.to}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-40">Télécharger bizexdoc.csv</button>
            </div>
          </form>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50" data-testid="delete-invoice-modal">
          <div className="bg-white rounded-lg border border-slate-200 shadow-lg w-full max-w-md p-6 space-y-4">
            <h2 className="text-base font-semibold text-slate-900">Supprimer la facture {deleteTarget.number} ?</h2>
            <div className="text-sm text-slate-600 space-y-1" data-testid="delete-invoice-summary">
              <div>Client : <b>{deleteTarget.client_name}</b></div>
              <div>Montant : <b>{fmtCHF(deleteTarget.total_payable ?? deleteTarget.total_ttc)}</b></div>
            </div>
            {deleteTarget.status === "payee" && (
              <p className="text-xs font-semibold text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="delete-invoice-paid-warning">
                Attention : cette facture est marquée PAYÉE{deleteTarget.payment_method ? ` (${deleteTarget.payment_method})` : ""}.
              </p>
            )}
            <p className="text-xs text-slate-500">Les bons liés repasseront au statut « Réalisé » et redeviendront facturables, sans perdre leurs lignes ni leurs prix.</p>
            <div className="flex justify-end gap-2 pt-2">
              <button data-testid="delete-invoice-cancel" onClick={() => setDeleteTarget(null)} className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100">Annuler</button>
              <button data-testid="delete-invoice-confirm" onClick={confirmDelete} className="px-4 py-2 rounded-lg text-sm font-semibold bg-red-600 text-white hover:bg-red-700">Supprimer définitivement</button>
            </div>
          </div>
        </div>
      )}

      {issueTarget && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50" data-testid="issue-date-modal">
          <form onSubmit={submitIssueDate} className="bg-white rounded-lg border border-slate-200 shadow-lg w-full max-w-sm p-6 space-y-4">
            <h2 className="text-base font-semibold text-slate-900">Modifier la date de {issueTarget.number}</h2>
            {issueTarget.status === "payee" && (
              <p className="text-xs font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="issue-date-paid-warning">Cette facture est déjà payée. Modifier quand même ?</p>
            )}
            {issueTarget.sent_date && (
              <p className="text-xs font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="issue-date-sent-warning">Cette facture est déjà envoyée (le {fmtDate(issueTarget.sent_date)}). Modifier quand même ?</p>
            )}
            <DateField label="Date de facture *" testid="issue-date-form-date" required value={issueForm.date} onChange={(v) => setIssueForm({ date: v })} />
            {issueFuture && <p className="text-xs font-semibold text-red-600" data-testid="issue-date-future-error">Une date future est refusée.</p>}
            {issueAfterSent && <p className="text-xs font-semibold text-red-600" data-testid="issue-date-sent-error">La date d'envoi ({fmtDate(issueTarget.sent_date)}) ne peut pas précéder la date de facture.</p>}
            <p className="text-xs text-slate-500">L'échéance sera recalculée à partir de la nouvelle date. Le numéro, les montants et le statut de paiement ne changent pas.</p>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" data-testid="issue-date-cancel" onClick={() => setIssueTarget(null)} className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100">Annuler</button>
              <button type="submit" data-testid="issue-date-submit" disabled={issueFuture || issueAfterSent}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-40">
                {issueTarget.status === "payee" || issueTarget.sent_date ? "Modifier quand même" : "Enregistrer"}
              </button>
            </div>
          </form>
        </div>
      )}

      {sentTarget && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50" data-testid="sent-modal">
          <form onSubmit={submitSent} className="bg-white rounded-lg border border-slate-200 shadow-lg w-full max-w-sm p-6 space-y-4">
            <h2 className="text-base font-semibold text-slate-900">Marquer {sentTarget.number} comme envoyée</h2>
            <DateField label="Date d'envoi *" testid="sent-form-date" required value={sentForm.date} onChange={(v) => setSentForm({ date: v })} />
            {sentFuture && <p className="text-xs font-semibold text-red-600" data-testid="sent-future-error">Une date future est refusée.</p>}
            {sentBeforeIssue && <p className="text-xs font-semibold text-red-600" data-testid="sent-before-issue-error">La date d'envoi ne peut pas être antérieure à la date de facture ({fmtDate(sentTarget.issue_date)}).</p>}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" data-testid="sent-cancel" onClick={() => setSentTarget(null)} className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100">Annuler</button>
              <button type="submit" data-testid="sent-submit" disabled={sentFuture || sentBeforeIssue}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-40">Valider</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between text-slate-600">
      <span>{label}</span><span className="font-medium">{fmtCHF(value)}</span>
    </div>
  );
}

function DateField({ label, testid, value, onChange, required = false }) {
  const toCh = (iso) => (iso && /^\d{4}-\d{2}-\d{2}/.test(iso) ? iso.slice(0, 10).split("-").reverse().join(".") : "");
  const [text, setText] = useState(toCh(value));
  useEffect(() => { setText(toCh(value)); }, [value]);
  const invalid = !!text && !/^\d{2}\.\d{2}\.\d{4}$/.test(text);
  const handle = (v) => {
    setText(v);
    const m = v.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    if (m) onChange(`${m[3]}-${m[2]}-${m[1]}`);
  };
  return (
    <div>
      <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</label>
      <input data-testid={testid} type="text" inputMode="numeric" placeholder="JJ.MM.AAAA" required={required}
        value={text} onChange={(e) => handle(e.target.value)}
        pattern="\d{2}\.\d{2}\.\d{4}" title="Format JJ.MM.AAAA"
        className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:ring-slate-900 focus:outline-none ${invalid ? "border-red-400 bg-red-50" : "border-slate-300"}`} />
      {invalid && <p className="text-[11px] text-red-600 mt-0.5">Format attendu : JJ.MM.AAAA</p>}
    </div>
  );
}
